import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { Status } from '../../../core/models/status';
import { StatusService } from '../../../core/services/status.service';
import { formatCamelCase } from '../../../core/utils/utils';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { StatusFormComponent } from './status-form/status-form.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { EmPermissionDirective } from '../../../shared/directives/has-permission.directive';
import { Permission } from '../../../core/enums/permission.enum';
import { PermissionService } from '../../../core/services/permission.service';
import { PermissionBit } from '../../../core/utils/permission-types';


@Component({
  selector: 'em-status',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    ToastModule,
    DialogModule,
    ConfirmDialogModule,
    TranslatePipe,
    EmPermissionDirective
  ],
  templateUrl: './status.component.html',
  styleUrl: './status.component.scss',
  providers: [MessageService, ConfirmationService],
})
export class StatusComponent  {
  statuses!: Status[];
  ref: DynamicDialogRef | undefined;
  translations: any = {};

  Permission = Permission;
  

  constructor(
    private statusService: StatusService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private dialogService: DialogService,
    private translate: TranslateService,
    private permissionService: PermissionService
  ) {}

  ngOnInit() {
    this.getAllStatuses();
    this.loadTranslations();
  }

  loadTranslations() {
    this.translate
      .get([
        'layout.configurations.status.title',
        'layout.configurations.status.actions',
        'layout.configurations.status.addStatus.title',
        'layout.configurations.status.editStatus.title',
        'layout.configurations.status.deleteStatus.title',
        'layout.configurations.status.deleteStatus.detail',
        'layout.configurations.status.deleteStatus.delete',
        'layout.configurations.status.deleteStatus.cancel',
        'layout.configurations.status.deleteStatus.successMessage.title',
        'layout.configurations.status.deleteStatus.successMessage.detail',
        'layout.configurations.status.deleteStatus.errorMessage.title',
        'layout.configurations.status.deleteStatus.errorMessage.detail',
        'layout.configurations.status.editStatus.errorMessage.title',
        'layout.configurations.status.editStatus.errorMessage.detail',
        'layout.configurations.status.addStatus.successMessage.title',
        'layout.configurations.status.addStatus.successMessage.detail',
        'layout.configurations.status.deleteStatus.infoMessage.title',
        'layout.configurations.status.deleteStatus.infoMessage.detail',
        'layout.configurations.status.editStatus.successMessage.title',
        'layout.configurations.status.editStatus.successMessage.detail',
        'layout.configurations.status.deleteStatus.cancelDeletion.title',
        'layout.configurations.status.deleteStatus.cancelDeletion.message',
      ])
      .subscribe((translations) => {
        this.translations = translations;
      });
  }

  getAllStatuses() {
    this.statusService.getStatuses().subscribe((data) => (this.statuses = data));
  }

  onAdd() {
    this.ref = this.dialogService.open(StatusFormComponent, {
      header: this.translations['layout.configurations.status.addStatus.title'],
      width: '320px',
      contentStyle: { overflow: 'auto' },
    });

    this.ref.onClose.subscribe((status) => {
      if (status) {
        this.getAllStatuses();
        this.messageService.add({
          severity: 'success',
          summary: this.translations['layout.configurations.status.addStatus.successMessage.title'],
          detail: this.translations['layout.configurations.status.addStatus.successMessage.detail'],
        });
      }
    });
  }

  onEdit(status: Status) {
    this.ref = this.dialogService.open(StatusFormComponent, {
      header: this.translations['layout.configurations.status.editStatus.title'],
      width: '320px',
      contentStyle: { overflow: 'auto' },
      data: status,
    });

    this.ref.onClose.subscribe((result) => {
    if (!result) return;
    if (result.type === 'no-change') {
      this.messageService.add({
        severity: 'warn',
        summary:this.translate.instant('layout.configurations.users.assignRolesToUser.roles.noChanges'),
        detail:this.translate.instant('layout.configurations.users.assignRolesToUser.roles.noUpdateMessage')
      });
      return;
    }  
      if (result.type === 'updated') {
        this.getAllStatuses();
        this.messageService.add({
          severity: 'success',
          summary: this.translations['layout.configurations.status.addStatus.successMessage.title'],
          detail: this.translations['layout.configurations.status.addStatus.successMessage.detail'],
        });
      }
    });
  }

  onDelete(event: Event, id: number) {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: this.translations['layout.configurations.status.deleteStatus.detail'],
      icon: 'pi pi-exclamation-triangle',
      header: this.translations['layout.configurations.status.deleteStatus.title'],
      acceptIcon: 'none',
      rejectIcon: 'none',
      acceptLabel: this.translations['layout.configurations.status.deleteStatus.delete'],
      rejectLabel: this.translations['layout.configurations.status.deleteStatus.cancel'],
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.deleteStatus(id);
      },
      reject: () => {
        this.messageService.add({
          severity: 'info',
          summary: this.translations['layout.configurations.status.deleteStatus.cancelDeletion.title'],
          detail: this.translations['layout.configurations.status.deleteStatus.cancelDeletion.message'],
          life: 3000,
        });
      },
    });
  }

  deleteStatus(id: number) {
    this.statusService.deleteStatus(id).subscribe({
      next: () => {
        this.getAllStatuses();
        this.messageService.add({
          severity: 'success',
          summary: this.translations['layout.configurations.status.deleteStatus.successMessage.title'],
          detail: this.translations['layout.configurations.status.deleteStatus.successMessage.detail'],
        });
      },
      error: (err) => {
        console.log('Error deleting status: ' + err);
        this.messageService.add({
          severity: 'error',
          summary: this.translations['layout.configurations.status.deleteStatus.errorMessage.title'],
          detail: this.translations['layout.configurations.status.deleteStatus.errorMessage.detail'],
        });
      },
    });
  }
}
